import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Checkbox,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import Swal from "sweetalert2";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";

import {
  AllVendors,
  ProductCategoryQuery,
  AllPurchaseOrderItems,
  PaymentMethodQuery,
} from "../../../data/Axios/queries";
import {
  setupManagementUrl,
  salesManagement,
  AccountsandFinanceurl,
} from "../../../config";
import {
  graphqlQuery,
  generateAndExecuteMutation,
  generateAndExecuteMutationreturn,
} from "../../../data/Axios/DynamicService";

// ─────────────────────────────────────────────────────────────────────────
// Template column definitions
// ─────────────────────────────────────────────────────────────────────────
const PRODUCTS_SHEET_NAME = "System Products";
const INVENTORY_SHEET_NAME = "Inventory";
const CATEGORIES_SHEET_NAME = "Product categories";
const PRODUCT_CODES_SHEET_NAME = "Product Codes";

const TEMPLATE_COLUMNS = [
  "Product Id",
  "Category Id",
  "Product Name",
  "Product Code",
  "Average Buying Price",
  "Average Selling Price",
  "Is Service",
  "Label (dropdown source)",
  "Select Category ▼",
  "Quantity",
];

const REQUIRED_COLUMNS = [
  "Product Name",
  "Select Category ▼",
  "Average Buying Price",
  "Average Selling Price",
  "Quantity",
];

const AllProductViewsQuery = `
{
  allProductViews {
    id
    productName
    productCategory
    unitPrice
    costPrice
    retailPrice
    wholesalePrice
    isActive
    reorderPoint
    isService
  }
}`;

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────
const getTodayLocalDate = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toBool = (val) => {
  if (typeof val === "boolean") return val;
  const s = String(val ?? "").trim().toLowerCase();
  return s === "true" || s === "1" || s === "yes";
};

const toNumber = (val) => {
  const n = Number(val);
  return Number.isFinite(n) ? n : NaN;
};

const normalizeKey = (val) => String(val ?? "").trim().toLowerCase();

const STEPS = ["Download template", "Upload file", "Review & edit", "Submit"];

const InventoryBulkUpload = ({ branchId, companyId, onComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [categories, setCategories] = useState([]);
  const [existingProducts, setExistingProducts] = useState([]);
  const [productCodeOptions, setProductCodeOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [submitProgress, setSubmitProgress] = useState({ done: 0, total: 0 });
  const [uploadVendorId, setUploadVendorId] = useState(null);
  const [filterType, setFilterType] = useState("all");
  // NEW: default payment method + account used for auto-generated, auto-approved
  // payment vouchers created against each successful purchase order.
  const [defaultPayment, setDefaultPayment] = useState(null); // { paymentMethodId, paymentAccountId }
  const fileInputRef = useRef(null);
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const CompanyId = parseInt(decodedToken.CompanyId, 0);
  const BranchId = parseInt(decodedToken.BranchId, 0);
  const [uploadAuditResult, setUploadAuditResult] = useState(null);

  // ── Fetch upload vendor ─────────────────────────────────────────────
  const fetchUploadVendor = async () => {
    try {
      const response = await graphqlQuery(AllVendors, salesManagement.uri);
      const vendors = Array.isArray(response) ? response : response?.allVendors || [];
      const uploadVendor = vendors.find((v) =>
        v.vendorName?.toLowerCase().includes("upload")
      );
      if (!uploadVendor?.id) {
        throw new Error('No vendor found whose vendorName contains "upload"');
      }
      setUploadVendorId(uploadVendor.id);
    } catch (error) {
      console.error("Error fetching upload vendor:", error);
    }
  };

  // ── Fetch existing products via allProductsViews ────────────────────
  const fetchExistingProducts = async () => {
    try {
      const response = await graphqlQuery(AllProductViewsQuery, setupManagementUrl.uri);
      const list = Array.isArray(response)
        ? response
        : response?.allProductViews || [];
      setExistingProducts(
        list.filter((p) => p.isActive !== false && Number(p.isService) !== 1)
      );
    } catch (error) {
      console.error("Error fetching existing products:", error);
    }
  };

  // ── Fetch all purchase order items to build product code options ─────
  const fetchProductCodes = async () => {
    try {
      const response = await graphqlQuery(AllPurchaseOrderItems, salesManagement.uri);
      const items = Array.isArray(response) ? response : response?.allPurchaseOrderItems || [];

      const optionsMap = new Map();

      items.forEach((item) => {
        const productCode = String(item.productCode || "").trim();
        if (item.productId && productCode) {
          const key = `${item.productId}-${productCode}`;
          if (!optionsMap.has(key)) {
            optionsMap.set(key, { productId: item.productId, productCode });
          }
        }
      });

      const options = [];
      optionsMap.forEach((opt) => {
        const product = existingProducts.find(p => p.id === opt.productId);
        const productName = product?.productName || "Unknown Product";
        options.push({
          productId: opt.productId,
          productName,
          productCode: opt.productCode,
          label: `${productName} — ${opt.productCode}`,
          displayLabel: `${productName} — ${opt.productCode}`,
          codeOnly: opt.productCode,
        });
      });

      options.sort((a, b) => {
        const nameCompare = a.productName.localeCompare(b.productName);
        if (nameCompare !== 0) return nameCompare;
        return a.productCode.localeCompare(b.productCode);
      });

      setProductCodeOptions(options);
    } catch (error) {
      console.error("Error fetching purchase order items for product codes:", error);
    }
  };

  // ── NEW: Fetch a default payment method + account for auto-approved PVs ──
  const fetchDefaultPaymentMethod = async () => {
    try {
      const paymentMethods = await graphqlQuery(PaymentMethodQuery, AccountsandFinanceurl.uri);
      const list = Array.isArray(paymentMethods)
        ? paymentMethods
        : paymentMethods?.paymentMethod || [];

      // NOTE: adjust this match to whatever your "auto-paid" method is named
      const method =
        list.find((m) => m.accountName?.toLowerCase().includes("cash")) || list[0];

      if (!method?.id) {
        throw new Error("No payment method found to use for auto-approved vouchers");
      }

      const accountQuery = `{
        accountsBySubCategoryId(functionparams: { accountSubcategoryId: ${method.id} }) {
          id
          accountName
        }
      }`;
      const accounts = await graphqlQuery(accountQuery, AccountsandFinanceurl.uri);
      const accountList = Array.isArray(accounts)
        ? accounts
        : accounts?.accountsBySubCategoryId || [];

      if (!accountList[0]?.id) {
        throw new Error("No account found under the default payment method");
      }

      setDefaultPayment({
        paymentMethodId: method.id,
        paymentAccountId: accountList[0].id,
      });
    } catch (error) {
      console.error("Error fetching default payment method:", error);
    }
  };

  // ── Load reference data ─────────────────────────────────────────────
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const categoryResponse = await graphqlQuery(
          ProductCategoryQuery,
          setupManagementUrl.uri
        );
        const list = Array.isArray(categoryResponse)
          ? categoryResponse
          : categoryResponse?.productCategory || [];
        setCategories(list.filter((c) => c.isActive !== false));
        await fetchExistingProducts();
        await fetchUploadVendor();
        await fetchDefaultPaymentMethod();
      } catch (error) {
        console.error("Error fetching reference data:", error);
      }
    };
    loadReferenceData();
  }, []);

  useEffect(() => {
    if (existingProducts.length > 0) {
      fetchProductCodes();
    }
  }, [existingProducts]);

  // ── Find product by product code option ─────────────────────────────
  const findProductByCodeOption = useCallback((labelOrCode) => {
    const normalized = normalizeKey(labelOrCode);
    return (
      productCodeOptions.find(opt => normalizeKey(opt.label) === normalized) ||
      productCodeOptions.find(opt => normalizeKey(opt.displayLabel) === normalized) ||
      productCodeOptions.find(opt => normalizeKey(opt.codeOnly) === normalized) ||
      productCodeOptions.find(opt => normalizeKey(opt.productName) === normalized)
    );
  }, [productCodeOptions]);

  // ── Find existing product by code or name ──────────────────────────
  const findExistingProduct = useCallback(
    (productCode, productName) => {
      const codeKey = normalizeKey(productCode);
      const nameKey = normalizeKey(productName);

      if (codeKey) {
        const byCodeOpt = findProductByCodeOption(productCode);
        if (byCodeOpt) {
          const product = existingProducts.find(p => p.id === byCodeOpt.productId);
          if (product) return { ...product, matchedProductCode: byCodeOpt.productCode };
        }
      }

      if (nameKey) {
        return existingProducts.find(
          (p) => normalizeKey(p.productName) === nameKey
        );
      }
      return undefined;
    },
    [existingProducts, productCodeOptions, findProductByCodeOption]
  );

  // ── Category lookups ─────────────────────────────────────────────────
  const categoryByLabel = useCallback(
    (label) => {
      const raw = String(label ?? "").trim();
      const idMatch = raw.match(/^(\d+)/);
      if (idMatch) {
        const id = Number(idMatch[1]);
        const found = categories.find((c) => Number(c.id) === id);
        if (found) return found;
      }
      return categories.find(
        (c) => c.name?.trim().toLowerCase() === raw.toLowerCase()
      );
    },
    [categories]
  );

  const categoryById = useCallback(
    (idOrNameOrObj) => {
      if (idOrNameOrObj === null || idOrNameOrObj === undefined || idOrNameOrObj === "") {
        return undefined;
      }
      const candidate =
        typeof idOrNameOrObj === "object"
          ? idOrNameOrObj.id ?? idOrNameOrObj.categoryId ?? idOrNameOrObj.name ?? idOrNameOrObj.categoryName
          : idOrNameOrObj;

      const numericId = Number(candidate);
      if (Number.isFinite(numericId)) {
        const byId = categories.find((c) => Number(c.id) === numericId);
        if (byId) return byId;
      }
      return categories.find(
        (c) => normalizeKey(c.name) === normalizeKey(candidate)
      );
    },
    [categories]
  );

  const getExistingBuyingPrice = (p) => p.costPrice ?? 0;
  const getExistingSellingPrice = (p) => p.retailPrice ?? p.unitPrice ?? 0;
  const getExistingCategoryRef = (p) => p.productCategory ?? null;

  // ── Template generation ──────────────────────────────────────────────
  const buildExampleRow = () => {
    const exampleCategory = categories[0];
    const exampleLabel = exampleCategory ? exampleCategory.name : "";
    return {
      productId: "",
      categoryId: "",
      productName: "Mayar Perfume 50ml",
      productCode: "Mayar",
      buyingPrice: "300",
      sellingPrice: "600",
      isService: "FALSE",
      label: "",
      categoryLabel: exampleLabel,
      quantity: "10",
    };
  };

  const downloadCsvTemplate = () => {
    const ex = buildExampleRow();
    const existingRows = existingProducts.map((p) => {
      const cat = categoryById(getExistingCategoryRef(p));
      return [
        p.id,
        getExistingCategoryRef(p) ?? "",
        p.productName ?? "",
        "",
        getExistingBuyingPrice(p),
        getExistingSellingPrice(p),
        p.isService ? "TRUE" : "FALSE",
        "",
        cat ? cat.name : "",
        "",
      ];
    });
    const csv = Papa.unparse({
      fields: TEMPLATE_COLUMNS,
      data: [
        ...existingRows,
        [
          ex.productId, ex.categoryId, ex.productName, ex.productCode,
          ex.buyingPrice, ex.sellingPrice, ex.isService, ex.label,
          ex.categoryLabel, ex.quantity,
        ],
      ],
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "product_inventory_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadExcelTemplate = async () => {
    const wb = new ExcelJS.Workbook();
    const lastCatRow = Math.max(categories.length + 1, 2);

    // ── Product categories sheet ──────────────────────────────────────
    const catSheet = wb.addWorksheet(CATEGORIES_SHEET_NAME);
    catSheet.addRow(["CategoryId", "Name"]);
    categories.forEach((c) => {
      catSheet.addRow([c.id, c.name]);
    });
    catSheet.getRow(1).font = { bold: true };
    catSheet.columns = [{ width: 14 }, { width: 24 }];

    // ── System Products sheet ─────────────────────────────────────────
    const prodSheet = wb.addWorksheet(PRODUCTS_SHEET_NAME);
    prodSheet.addRow([
      "Product Id", "Category Id", "Product Name", "Product Code",
      "Average Buying Price", "Average Selling Price", "Is Service",
      "Label (dropdown source)", "Select Category ▼",
    ]);
    prodSheet.getRow(1).font = { bold: true };
    prodSheet.columns = [
      { width: 12 }, { width: 12 }, { width: 26 }, { width: 16 },
      { width: 18 }, { width: 18 }, { width: 12 }, { width: 45 }, { width: 26 },
    ];
    prodSheet.getColumn(1).hidden = true;
    prodSheet.getColumn(2).hidden = true;

    const ex = buildExampleRow();

    let row = 2;
    existingProducts.forEach((p) => {
      const cat = categoryById(getExistingCategoryRef(p));
      prodSheet.getCell(`A${row}`).value = p.id;
      prodSheet.getCell(`C${row}`).value = p.productName ?? "";
      prodSheet.getCell(`D${row}`).value = "";
      prodSheet.getCell(`E${row}`).value = Number(getExistingBuyingPrice(p));
      prodSheet.getCell(`F${row}`).value = Number(getExistingSellingPrice(p));
      prodSheet.getCell(`G${row}`).value = !!p.isService;
      prodSheet.getCell(`I${row}`).value = cat ? cat.name : "";
      row += 1;
    });

    const firstNewRow = row;
    prodSheet.getCell(`C${firstNewRow}`).value = ex.productName;
    prodSheet.getCell(`D${firstNewRow}`).value = ex.productCode;
    prodSheet.getCell(`E${firstNewRow}`).value = Number(ex.buyingPrice);
    prodSheet.getCell(`F${firstNewRow}`).value = Number(ex.sellingPrice);
    prodSheet.getCell(`G${firstNewRow}`).value = ex.isService === "TRUE";
    prodSheet.getCell(`I${firstNewRow}`).value = ex.categoryLabel;

    const lastRow = Math.max(firstNewRow + 198, 200);
    for (let r = 2; r <= lastRow; r += 1) {
      prodSheet.getCell(`B${r}`).value = {
        formula:
          `IF(I${r}="","",IFERROR(INDEX('${CATEGORIES_SHEET_NAME}'!$A$2:$A$${lastCatRow},` +
          `MATCH(I${r},'${CATEGORIES_SHEET_NAME}'!$B$2:$B$${lastCatRow},0)),""))`,
      };
      prodSheet.getCell(`H${r}`).value = {
        formula: `IF(C${r}="","",C${r}&IF(D${r}="",""," — "&D${r}))`,
      };
      prodSheet.getCell(`I${r}`).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`'${CATEGORIES_SHEET_NAME}'!$B$2:$B$${lastCatRow}`],
        showErrorMessage: true,
        errorTitle: "Invalid Category",
        error: "Please pick a category from the dropdown list.",
      };
    }

    // ── Product Codes sheet ───────────────────────────────────────────
    const codesSheet = wb.addWorksheet(PRODUCT_CODES_SHEET_NAME);
    codesSheet.addRow(["Product Id", "Product Name", "Product Code", "Display Label"]);
    codesSheet.getRow(1).font = { bold: true };
    codesSheet.columns = [{ width: 12 }, { width: 26 }, { width: 16 }, { width: 45 }];
    codesSheet.getColumn(1).hidden = true;
    codesSheet.getColumn(2).hidden = true;
    codesSheet.getColumn(3).hidden = true;

    let codeRow = 2;

    productCodeOptions.forEach((opt) => {
      codesSheet.getCell(`A${codeRow}`).value = opt.productId;
      codesSheet.getCell(`B${codeRow}`).value = opt.productName;
      codesSheet.getCell(`C${codeRow}`).value = opt.productCode;
      codesSheet.getCell(`D${codeRow}`).value = opt.label;
      codeRow += 1;
    });

    for (let r = 2; r <= lastRow; r++) {
      codesSheet.getCell(`A${codeRow}`).value = {
        formula: `IF('${PRODUCTS_SHEET_NAME}'!A${r}="","",'${PRODUCTS_SHEET_NAME}'!A${r})`
      };
      codesSheet.getCell(`B${codeRow}`).value = {
        formula: `IF('${PRODUCTS_SHEET_NAME}'!C${r}="","",'${PRODUCTS_SHEET_NAME}'!C${r})`
      };
      codesSheet.getCell(`C${codeRow}`).value = {
        formula: `IF('${PRODUCTS_SHEET_NAME}'!D${r}="","",'${PRODUCTS_SHEET_NAME}'!D${r})`
      };
      codesSheet.getCell(`D${codeRow}`).value = {
        formula: `IF('${PRODUCTS_SHEET_NAME}'!C${r}="","",'${PRODUCTS_SHEET_NAME}'!C${r}&IF('${PRODUCTS_SHEET_NAME}'!D${r}="",""," — "&'${PRODUCTS_SHEET_NAME}'!D${r}))`
      };
      codeRow += 1;
    }

    const lastCodeRow = codeRow - 1;

    // ── Inventory sheet ───────────────────────────────────────────────
    const invSheet = wb.addWorksheet(INVENTORY_SHEET_NAME);
    invSheet.addRow(["Product Id", "Quantity", "Select Product ▼", "Product Code"]);
    invSheet.getRow(1).font = { bold: true };
    invSheet.columns = [{ width: 12 }, { width: 14 }, { width: 45 }, { width: 16 }];
    invSheet.getColumn(1).hidden = true;
    invSheet.getColumn(4).hidden = true;

    invSheet.getCell(`B2`).value = Number(ex.quantity);
    invSheet.getCell(`C2`).value = `${ex.productName} — ${ex.productCode}`;

    for (let r = 2; r <= lastRow; r += 1) {
      invSheet.getCell(`A${r}`).value = {
        formula:
          `IF(C${r}="","",IFERROR(INDEX('${PRODUCT_CODES_SHEET_NAME}'!$A$2:$A$${lastCodeRow},` +
          `MATCH(C${r},'${PRODUCT_CODES_SHEET_NAME}'!$D$2:$D$${lastCodeRow},0)),""))`,
      };
      invSheet.getCell(`D${r}`).value = {
        formula:
          `IF(C${r}="","",IFERROR(INDEX('${PRODUCT_CODES_SHEET_NAME}'!$C$2:$C$${lastCodeRow},` +
          `MATCH(C${r},'${PRODUCT_CODES_SHEET_NAME}'!$D$2:$D$${lastCodeRow},0)),` +
          `IFERROR(MID(C${r},FIND(" — ",C${r})+3,LEN(C${r})),"")))`,
      };
      invSheet.getCell(`C${r}`).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`'${PRODUCT_CODES_SHEET_NAME}'!$D$2:$D$${lastCodeRow}`],
        showErrorMessage: true,
        errorTitle: "Invalid Product",
        error: "Please pick a product from the dropdown list.",
      };
    }

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "product_inventory_template.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── File parsing ─────────────────────────────────────────────────────
  const parseCsvFile = (file) =>
    new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => resolve(result.data),
        error: reject,
      });
    });

  const parseExcelFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: "array" });

          const findSheet = (needle) =>
            wb.SheetNames.find((n) => n.toLowerCase().includes(needle));

          const productsSheetName =
            findSheet("system product") || findSheet("product") || wb.SheetNames[0];
          const inventorySheetName = findSheet("inventory");

          // FIX 1: raw: false forces XLSX to return evaluated cell values instead of
          // formula objects. Without this, formula cells (Product Id, Product Code,
          // Quantity) return { formula: "...", result: undefined } which makes pid
          // always empty and quantities always NaN — silently breaking all matching.
          const productRows = XLSX.utils.sheet_to_json(
            wb.Sheets[productsSheetName],
            { defval: "", raw: false }
          );

          // FIX 1 (same): apply raw: false to Inventory sheet so Quantity and
          // Product Id formula cells resolve to their computed values.
          const inventoryRows = inventorySheetName
            ? XLSX.utils.sheet_to_json(wb.Sheets[inventorySheetName], { defval: "", raw: false })
            : [];

          // Build lookup maps from Inventory sheet
          const invDataByProductId = new Map();
          const invDataByProductLabel = new Map();
          const invDataByProductCode = new Map();

          inventoryRows.forEach((r) => {
            const pid = r["Product Id"];
            const prodLabel = r["Select Product ▼"];
            const prodCode = r["Product Code"];

            if (pid !== "" && pid !== undefined && pid !== null) {
              invDataByProductId.set(String(pid).trim(), {
                quantity: r["Quantity"],
                productCode: prodCode,
                productLabel: prodLabel,
              });
            }
            if (prodLabel) {
              invDataByProductLabel.set(normalizeKey(prodLabel), {
                quantity: r["Quantity"],
                productCode: prodCode,
                productLabel: prodLabel,
              });
            }
            if (prodCode) {
              invDataByProductCode.set(normalizeKey(prodCode), {
                quantity: r["Quantity"],
                productCode: prodCode,
                productLabel: prodLabel,
              });
            }
          });

          const merged = productRows
            .filter((r) => String(r["Product Name"] ?? "").trim() !== "")
            .map((r) => {
              const pid = String(r["Product Id"] ?? "").trim();
              const nameKey = normalizeKey(r["Product Name"]);
              const productCode = String(r["Product Code"] ?? "").trim();

              const expectedLabel = productCode
                ? `${r["Product Name"]} — ${productCode}`
                : r["Product Name"];
              const labelKey = normalizeKey(expectedLabel);

              let invData = { quantity: "", productCode: "", productLabel: "" };

              if (pid && invDataByProductId.has(pid)) {
                invData = invDataByProductId.get(pid);
              } else if (labelKey && invDataByProductLabel.has(labelKey)) {
                invData = invDataByProductLabel.get(labelKey);
              } else if (productCode && invDataByProductCode.has(normalizeKey(productCode))) {
                invData = invDataByProductCode.get(normalizeKey(productCode));
              } else if (nameKey && invDataByProductLabel.has(nameKey)) {
                invData = invDataByProductLabel.get(nameKey);
              }

              let resolvedCode = invData.productCode || r["Product Code"];
              if (!resolvedCode && invData.productLabel) {
                const codeMatch = invData.productLabel.match(/ — (.+)$/);
                if (codeMatch) resolvedCode = codeMatch[1];
              }

              return {
                "Product Id": pid,
                "Product Name": r["Product Name"],
                "Select Category ▼": r["Select Category ▼"],
                "Average Buying Price": r["Average Buying Price"],
                "Average Selling Price": r["Average Selling Price"],
                "Product Code": r["Product Code"] || resolvedCode,
                "Is Service": r["Is Service"],
                Quantity: invData.quantity,
              };
            });

          resolve(merged);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });

  // ── Row validation ───────────────────────────────────────────────────
  const validateRow = useCallback(
    (raw, index, existingRowNumber) => {
      const errors = [];

      const productName = String(raw["Product Name"] ?? "").trim();
      const categoryLabel = String(raw["Select Category ▼"] ?? "").trim();
      const productCode = String(raw["Product Code"] ?? "").trim();
      const buyingPriceRaw = raw["Average Buying Price"];
      const sellingPriceRaw = raw["Average Selling Price"];
      const isService = toBool(raw["Is Service"]);
      const quantityRaw = raw["Quantity"];

      // ── VALIDATION: Buying Price must be provided ───────────────────
      const buyingPriceStr = String(buyingPriceRaw ?? "").trim();
      const isBuyingPriceBlank = buyingPriceStr === "";
      const costPrice = toNumber(buyingPriceRaw);

      const sellingPriceStr = String(sellingPriceRaw ?? "").trim();
      const isSellingPriceBlank = sellingPriceStr === "";
      const sellingPrice = toNumber(sellingPriceRaw);

      const quantityStr = String(quantityRaw ?? "").trim();
      const quantity = toNumber(quantityRaw);

      let productId = String(raw["Product Id"] ?? "").trim();
      let matchedProductCode = productCode;

      let cleanProductName = productName;
      const nameCodeMatch = productName.match(/^(.+?)\s*—\s*(.+)$/);
      if (nameCodeMatch) {
        cleanProductName = nameCodeMatch[1].trim();
      }

      if (!productId) {
        const matched = findExistingProduct(productCode, cleanProductName);
        if (matched) {
          productId = String(matched.id);
          if (matched.matchedProductCode) matchedProductCode = matched.matchedProductCode;
          else if (matched.productCode) matchedProductCode = matched.productCode;
        }
      }
      const isUpdate = productId !== "" && !Number.isNaN(Number(productId));

      let resolvedCategory = categoryByLabel(categoryLabel);
      if (!resolvedCategory && productId) {
        const existingProd = existingProducts.find(
          (p) => String(p.id) === String(productId)
        );
        if (existingProd?.productCategory) {
          resolvedCategory = categoryById(existingProd.productCategory);
        }
      }

      if (!cleanProductName) errors.push("Product Name is required");

      if (!categoryLabel && !resolvedCategory) {
        errors.push("Select Category is required");
      } else if (!resolvedCategory) {
        errors.push(`Category "${categoryLabel}" not found`);
      }

      // ── Buying Price validation ──────────────────────────────────────
      if (isBuyingPriceBlank) {
        errors.push("Average Buying Price is required");
      } else if (Number.isNaN(costPrice)) {
        errors.push("Average Buying Price must be a number");
      }

      // ── Selling Price validation ─────────────────────────────────────
      if (isSellingPriceBlank) {
        errors.push("Average Selling Price is required");
      } else if (Number.isNaN(sellingPrice)) {
        errors.push("Average Selling Price must be a number");
      }

      if (!Number.isNaN(sellingPrice) && !Number.isNaN(costPrice) && sellingPrice < costPrice) {
        errors.push("Average Selling Price cannot be less than Average Buying Price");
      }

      const isQuantityBlank = quantityStr === "";

      if (isUpdate && isQuantityBlank) {
        // Existing product with no quantity = skip, not error
      } else if (Number.isNaN(quantity) || quantity < 0) {
        errors.push("Quantity must be a non-negative number");
      }

      return {
        rowNumber: existingRowNumber ?? index + 2,
        productId: isUpdate ? Number(productId) : null,
        isUpdate,
        productName: cleanProductName,
        productCode: matchedProductCode,
        categoryLabel: resolvedCategory?.name ?? categoryLabel,
        categoryId: resolvedCategory?.id ?? null,
        costPrice,
        sellingPrice,
        isService,
        quantity: isUpdate && isQuantityBlank ? 0 : quantity,
        quantityBlank: isUpdate && isQuantityBlank,
        errors,
        isValid: errors.length === 0,
      };
    },
    [categoryByLabel, categoryById, existingProducts, findExistingProduct]
  );

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setActiveStep(1);
    setIsParsing(true);
    setFilterType("all");

    try {
      const isExcel = /\.xlsx?$/i.test(file.name);
      const rawRows = isExcel ? await parseExcelFile(file) : await parseCsvFile(file);

      const missingCols = REQUIRED_COLUMNS.filter(
        (col) => !(col in (rawRows[0] || {}))
      );
      if (missingCols.length > 0) {
        Swal.fire({
          icon: "error",
          title: "Missing columns",
          text: `Your file is missing: ${missingCols.join(", ")}. Please use the provided template.`,
        });
        setIsParsing(false);
        return;
      }

      const validated = rawRows.map((r, i) => validateRow(r, i));
      setRows(validated);
      setActiveStep(2);
    } catch (error) {
      console.error("Error parsing file:", error);
      Swal.fire({
        icon: "error",
        title: "Could not read file",
        text: "Please check the file format and try again.",
      });
    } finally {
      setIsParsing(false);
      e.target.value = "";
    }
  };

  const removeRow = (rowNumber) => {
    setRows((prev) => prev.filter((r) => r.rowNumber !== rowNumber));
  };

  const updateRowField = (rowNumber, field, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.rowNumber !== rowNumber) return r;
        const raw = {
          "Product Id": r.isUpdate ? String(r.productId) : "",
          "Product Name": r.productName,
          "Product Code": r.productCode,
          "Select Category ▼": r.categoryLabel,
          "Average Buying Price": r.costPrice,
          "Average Selling Price": r.sellingPrice,
          "Is Service": r.isService ? "TRUE" : "FALSE",
          Quantity: r.quantityBlank ? "" : r.quantity,
        };
        raw[field] = value;
        return validateRow(raw, 0, r.rowNumber);
      })
    );
  };

  const validRows = rows.filter((r) => r.isValid);
  const invalidRows = rows.filter((r) => !r.isValid);

  // FIX 2: Include ALL valid non-skipped rows in submitRows.
  // Previously `quantity > 0` here was silently dropping existing products
  // whose quantity came back as 0 due to formula cells not resolving (fixed
  // by raw:false above), or legitimate qty=0 edge cases.
  // The PO creation gate inside submitAll (`row.quantity > 0`) is sufficient
  // — we don't need to filter here as well.
  const submitRows = validRows.filter((r) => !r.quantityBlank);

  const createCount = submitRows.filter((r) => !r.isUpdate).length;
  const updateCount = submitRows.filter((r) => r.isUpdate).length;
  const skippedCount = validRows.length - submitRows.length;

  // Filter logic for display
  const filteredRows = rows.filter((r) => {
    switch (filterType) {
      case "new":
        return !r.isUpdate;
      case "update":
        return r.isUpdate && !r.quantityBlank;
      case "skipped":
        return r.isUpdate && r.quantityBlank;
      case "error":
        return !r.isValid;
      default:
        return true;
    }
  });

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setFilterType(newFilter);
    }
  };

  // ── NEW: create + auto-approve a payment voucher for a confirmed PO ────
  const createAndApprovePaymentVoucher = async ({ purchaseOrderId, vendorId, totalAmount }) => {
    if (!defaultPayment) {
      throw new Error("Default payment method/account is not configured.");
    }

    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const currentTime = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    const combinedDateTime = `${getTodayLocalDate()}T${currentTime}`;

    // ── Create the voucher ────────────────────────────────────────────
    const pvMutationData = {
      purchaseOrderId: parseInt(purchaseOrderId, 10),
      vendorId: parseInt(vendorId, 10),
      paymentMethod: defaultPayment.paymentMethodId,
      amountPaid: parseFloat(totalAmount),
      paymentAccountId: defaultPayment.paymentAccountId,
      paymentDate: combinedDateTime,
      reference: "",
      remarks: "Auto-generated from bulk inventory upload",
      isUploaded: 1, // flag on the PV
    };

    const pvResponse = await generateAndExecuteMutation(
      "createPaymentVoucher",
      pvMutationData,
      "newPaymentVoucher",
      salesManagement.uri
    );

    const createdVoucher = Object.values(pvResponse || {})[0];
    const paymentVoucherId = createdVoucher?.id ?? createdVoucher?.paymentVoucherId;

    if (!paymentVoucherId) {
      throw new Error("Payment voucher created but no id was returned.");
    }

    // ── Auto-approve it (flag: 1 = approve, matching the existing approve handler) ──
    await generateAndExecuteMutationreturn(
      "approvePaymentVoucher",
      { id: paymentVoucherId, flag: 1 },
      "id",
      "approvePaymentVoucher",
      salesManagement.uri
    );

    return paymentVoucherId;
  };

  // ── Submission ──────────────────────────────────────────────────────
  const submitAll = async () => {
    if (!uploadVendorId) {
      Swal.fire({
        icon: "error",
        title: "Vendor Missing",
        text: "Could not find the Upload vendor.",
      });
      return;
    }
    if (submitRows.length === 0) return;

    setIsSubmitting(true);
    setSubmitProgress({ done: 0, total: submitRows.length });
     const failures = [];
      let successCount = 0;

      const uploadAudit = {
        successful: [],
        skipped: [],
        failed: [],
      };
     // Record rows skipped before submission
      rows.forEach((row) => {
        if (!row.isValid) {
          uploadAudit.skipped.push({
            rowNumber: row.rowNumber,
            productName: row.productName,
            action: row.isUpdate ? "Update" : "Create",
            reason: row.errors.join(", "),
          });
          return;
        }

        if (row.quantityBlank) {
          uploadAudit.skipped.push({
            rowNumber: row.rowNumber,
            productName: row.productName,
            action: "Update",
            reason: "Existing product with no quantity supplied.",
          });
        }
      });
   
      const skippedBeforeSubmit =
    rows.length -
    submitRows.length -
    invalidRows.length;

    if (skippedBeforeSubmit > 0) {
        console.warn(
            `${skippedBeforeSubmit} rows will not be submitted.`
        );
    }

    for (const row of submitRows) {
      // NEW: track the payment voucher id created for this row (if any)
      let rowPaymentVoucherId = null;
      let rowPaymentVoucherError = null;

      try {
        if (row.isUpdate) {
          // ── Update existing product metadata ──────────────────────
          const updateMutationData = {
            id: row.productId,
            productName: row.productName,
            productCategory: row.categoryId,
            unitPrice: row.sellingPrice,
            costPrice: row.costPrice,
            retailPrice: row.sellingPrice,
            wholesalePrice: row.sellingPrice,
            isActive: true,
            companyId: CompanyId,
            isService: row.isService ? 1 : 0,
            isInventory: true,
          };

          await generateAndExecuteMutation(
            "updateProducts",
            updateMutationData,
            "products",
            setupManagementUrl.uri
          );

          // ── Create PO to add stock for existing product ───────────
          // FIX 3: existing products with quantity now also go through
          // createPurchaseOrder + confirmPurchaseOrderDelivery, using
          // row.productId (the already-known id) instead of newProductId.
          if (row.quantity > 0 && !row.isService) {
            const now = new Date();
            const pad = (n) => String(n).padStart(2, "0");
            const currentTime = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
            const todayDate = getTodayLocalDate();
            const combinedDateTime = `${todayDate}T${currentTime}`;

            const fields = [
              `productId: ${row.productId}`,
              `vendorId: ${uploadVendorId}`,
              `cartons: 1`,
              `quantityPerCarton: ${row.quantity}`,
              `buyingPricePairs: ${Math.round(row.costPrice)}`,
              `sellingPricePairs: ${Math.round(row.sellingPrice)}`,
              `productCode: "${(row.productCode || row.productName).replace(/"/g, '\\"')}"`,
              `stockDueDate: "${combinedDateTime}"`,
              `createdDate: "${combinedDateTime}"`,
              `isActive: true`,
              `branchId: ${branchId || BranchId}`,
              `isBackDated: 0`,
              `isExternalWarehouse: 1`,
              `reorderPoint: 1`,
              `isUploaded: 1`,
            ];

            const createPoMutation = `
              mutation {
                createPurchaseOrder(
                  purchaseOrderItems: [{ ${fields.join(", ")} }]
                ) {
                  id
                  totalAmount
                  orderDate
                }
              }
            `;

            const poResponse = await graphqlQuery(createPoMutation, salesManagement.uri);
            const purchaseOrder = poResponse?.createPurchaseOrder || poResponse;
            const purchaseOrderId = purchaseOrder?.id;

            if (!purchaseOrderId) {
              throw new Error("Purchase order created but no purchase order id was returned.");
            }

            const confirmDeliveryMutation = `
              mutation {
                confirmPurchaseOrderDelivery(purchaseOrderId: ${purchaseOrderId}) {
                  id
                  deliveryStatus
                }
              }
            `;

            await graphqlQuery(confirmDeliveryMutation, salesManagement.uri);

            // ── NEW: auto-create + auto-approve the payment voucher ────
            try {
              rowPaymentVoucherId = await createAndApprovePaymentVoucher({
                purchaseOrderId,
                vendorId: uploadVendorId,
                totalAmount:
                  purchaseOrder?.totalAmount ?? row.quantity * row.costPrice,
              });
            } catch (pvError) {
              console.error(`Row ${row.rowNumber} PO succeeded but PV failed:`, pvError);
              rowPaymentVoucherError =
                pvError?.response?.data?.errors?.[0]?.message ||
                pvError?.message ||
                "Unknown error creating/approving payment voucher";
            }
          }

        } else {
          // ── Create new product ────────────────────────────────────
          const productMutationData = {
            productName: row.productName,
            productCategory: row.categoryId,
            unitPrice: row.sellingPrice,
            costPrice: row.costPrice,
            retailPrice: row.sellingPrice,
            wholesalePrice: row.sellingPrice,
            isInventory: true,
            isActive: true,
            companyId: CompanyId,
            isService: row.isService ? 1 : 0,
          };

          const productResponse = await generateAndExecuteMutation(
            "createProducts",
            productMutationData,
            "products",
            setupManagementUrl.uri
          );

          const createdProduct = Object.values(productResponse || {})[0];
          const newProductId = createdProduct?.id;

          if (!newProductId) {
            throw new Error("Product creation did not return an id.");
          }

          // ── Create PO to add stock for new product ────────────────
          if (row.quantity > 0 && !row.isService) {
            const now = new Date();
            const pad = (n) => String(n).padStart(2, "0");
            const currentTime = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
            const todayDate = getTodayLocalDate();
            const combinedDateTime = `${todayDate}T${currentTime}`;

            const fields = [
              `productId: ${newProductId}`,
              `vendorId: ${uploadVendorId}`,
              `cartons: 1`,
              `quantityPerCarton: ${row.quantity}`,
              `buyingPricePairs: ${Math.round(row.costPrice)}`,
              `sellingPricePairs: ${Math.round(row.sellingPrice)}`,
              `productCode: "${(row.productCode || row.productName).replace(/"/g, '\\"')}"`,
              `stockDueDate: "${combinedDateTime}"`,
              `createdDate: "${combinedDateTime}"`,
              `isActive: true`,
              `branchId: ${branchId || BranchId}`,
              `isBackDated: 0`,
              `isExternalWarehouse: 1`,
              `reorderPoint: 1`,
              `isUploaded: 1`,
            ];

            const createPoMutation = `
              mutation {
                createPurchaseOrder(
                  purchaseOrderItems: [{ ${fields.join(", ")} }]
                ) {
                  id
                  totalAmount
                  orderDate
                }
              }
            `;

            const poResponse = await graphqlQuery(createPoMutation, salesManagement.uri);
            const purchaseOrder = poResponse?.createPurchaseOrder || poResponse;
            const purchaseOrderId = purchaseOrder?.id;

            if (!purchaseOrderId) {
              throw new Error("Purchase order created but no purchase order id was returned.");
            }

            const confirmDeliveryMutation = `
              mutation {
                confirmPurchaseOrderDelivery(purchaseOrderId: ${purchaseOrderId}) {
                  id
                  deliveryStatus
                }
              }
            `;

            await graphqlQuery(confirmDeliveryMutation, salesManagement.uri);

            // ── NEW: auto-create + auto-approve the payment voucher ────
            try {
              rowPaymentVoucherId = await createAndApprovePaymentVoucher({
                purchaseOrderId,
                vendorId: uploadVendorId,
                totalAmount:
                  purchaseOrder?.totalAmount ?? row.quantity * row.costPrice,
              });
            } catch (pvError) {
              console.error(`Row ${row.rowNumber} PO succeeded but PV failed:`, pvError);
              rowPaymentVoucherError =
                pvError?.response?.data?.errors?.[0]?.message ||
                pvError?.message ||
                "Unknown error creating/approving payment voucher";
            }
          }
        }

        successCount++;

      uploadAudit.successful.push({
          rowNumber: row.rowNumber,
          productName: row.productName,
          action: row.isUpdate ? "Updated" : "Created",
          paymentVoucherId: rowPaymentVoucherId,
          paymentVoucherError: rowPaymentVoucherError,
      });
      } catch (error) {
        console.error(`Row ${row.rowNumber} failed:`, error);
        const failure = {
        rowNumber: row.rowNumber,
        productName: row.productName,
        action: row.isUpdate ? "Update" : "Create",
        message:
            error?.response?.data?.errors?.[0]?.message ||
            error?.message ||
            "Unknown error",
        };

        failures.push(failure);
        uploadAudit.failed.push(failure);
      } finally {
        setSubmitProgress((prev) => ({ ...prev, done: prev.done + 1 }));
      }
    }

    setIsSubmitting(false);
    setActiveStep(3);

   const buildList = (title, items, formatter) => {
  if (!items.length) return "";

        return `
          <br><br>
          <b>${title}</b>
          <hr style="margin:4px 0;">
          ${items
            .map(formatter)
            .join("<br><br>")}
        `;
      };

      // NEW: rows where a payment voucher was created & auto-approved
      const vouchersCreated = uploadAudit.successful.filter((r) => r.paymentVoucherId);
      // NEW: rows whose PO succeeded but the payment voucher step failed
      const voucherFailures = uploadAudit.successful.filter((r) => r.paymentVoucherError);

      const html = `
      <div style="text-align:left">

      <b>Upload Summary</b>

      <hr>

      <table style="width:100%">
      <tr>
      <td><b>Total Rows</b></td>
      <td>${rows.length}</td>
      </tr>

      <tr>
      <td style="color:green"><b>Successful</b></td>
      <td>${uploadAudit.successful.length}</td>
      </tr>

      <tr>
      <td style="color:green"><b>Payment Vouchers Created &amp; Approved</b></td>
      <td>${vouchersCreated.length}</td>
      </tr>

      <tr>
      <td style="color:orange"><b>Skipped</b></td>
      <td>${uploadAudit.skipped.length}</td>
      </tr>

      <tr>
      <td style="color:red"><b>Failed</b></td>
      <td>${uploadAudit.failed.length}</td>
      </tr>

      </table>

      ${buildList(
      "Payment Vouchers Created & Approved",
      vouchersCreated,
      (r)=>`
      <b>Row ${r.rowNumber}</b><br>
      ${r.productName}<br>
      Payment Voucher #${r.paymentVoucherId} — Approved
      `
      )}

      ${buildList(
      "Payment Voucher Issues",
      voucherFailures,
      (r)=>`
      <b>Row ${r.rowNumber}</b><br>
      ${r.productName}<br>
      Product/Stock succeeded, but voucher failed: ${r.paymentVoucherError}
      `
      )}

      ${buildList(
      "Skipped Rows",
      uploadAudit.skipped,
      (r)=>`
      <b>Row ${r.rowNumber}</b><br>
      ${r.productName}<br>
      Reason: ${r.reason}
      `
      )}

      ${buildList(
      "Failed Rows",
      uploadAudit.failed,
      (r)=>`
      <b>Row ${r.rowNumber}</b><br>
      ${r.productName}<br>
      Reason: ${r.message}
      `
      )}

      </div>
      `;
      setUploadAuditResult(uploadAudit);
      Swal.fire({
          icon:
              uploadAudit.failed.length > 0 || voucherFailures.length > 0
                  ? "warning"
                  : "success",

          title:
              uploadAudit.failed.length > 0 || voucherFailures.length > 0
                  ? "Upload Completed with Issues"
                  : "Upload Completed",

          width: 850,

          html,

          confirmButtonText: "OK",
      });

      onComplete &&
      onComplete({
          successCount,
          failures,
          uploadAudit,
      });

      if (uploadAudit.failed.length === 0 && voucherFailures.length === 0) {
          resetFlow();
      }
  };

  const resetFlow = () => {
    setRows([]);
    setFileName("");
    setActiveStep(0);
    setSubmitProgress({ done: 0, total: 0 });
    setFilterType("all");
  };

  // ─────────────────────────────────────────────────────────────────────
  return (
    <Paper elevation={3} sx={{ padding: 3, maxWidth: 1000, margin: "auto", mt: 5 }}>
      <Typography variant="h5" gutterBottom>
        Bulk Upload Products &amp; Inventory
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Guide */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          How this works
        </Typography>
        <Typography variant="body2" component="div">
          1. Download the template — existing products appear pre-filled.<br />
          2. Add new products in the <strong>System Products</strong> sheet (leave Product Id blank).<br />
          3. Select products from the <strong>Inventory</strong> sheet dropdown and set quantities.<br />
          4. Existing products with no quantity set will be skipped on submit.<br />
          5. Upload, review, and submit.
        </Typography>
      </Alert>

      {/* Step 1: Download template */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          1. Download a template
        </Typography>
        <Grid container spacing={2}>
          <Grid item>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadExcelTemplate}>
              Excel template (.xlsx)
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadCsvTemplate}>
              CSV template (.csv)
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Step 2: Upload */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          2. Upload your completed file
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          hidden
          onChange={handleFileSelected}
        />
        <Button
          variant="contained"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={isParsing}
        >
          {isParsing ? "Parsing..." : "Choose file"}
        </Button>
        {isParsing && (
          <CircularProgress size={20} sx={{ ml: 2, verticalAlign: "middle" }} />
        )}
        {fileName && !isParsing && (
          <Chip label={fileName} sx={{ ml: 2 }} onDelete={resetFlow} />
        )}
      </Box>

      {/* Loading state while parsing */}
      {isParsing && (
        <Box sx={{ mb: 3, textAlign: "center", py: 4 }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Parsing and validating your file...
          </Typography>
        </Box>
      )}

      {/* Step 3: Review & edit */}
      {rows.length > 0 && !isParsing && (
        <Box sx={{ mb: 3 }}>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            3. Review and edit before submitting
          </Typography>

          {/* Clickable filter badges */}
          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
            <ToggleButtonGroup
              value={filterType}
              exclusive
              onChange={handleFilterChange}
              size="small"
              aria-label="row filter"
            >
              <ToggleButton value="all" aria-label="all rows">
                <Chip
                  component="span"
                  size="small"
                  color={filterType === "all" ? "primary" : "default"}
                  variant={filterType === "all" ? "filled" : "outlined"}
                  label={`All (${rows.length})`}
                  sx={{ cursor: "pointer" }}
                />
              </ToggleButton>
              <ToggleButton value="new" aria-label="new products">
                <Chip
                  component="span"
                  size="small"
                  icon={<AddIcon />}
                  color={filterType === "new" ? "success" : "default"}
                  variant={filterType === "new" ? "filled" : "outlined"}
                  label={`New (${rows.filter(r => !r.isUpdate).length})`}
                  sx={{ cursor: "pointer" }}
                />
              </ToggleButton>
              <ToggleButton value="update" aria-label="updates">
                <Chip
                  component="span"
                  size="small"
                  icon={<EditIcon />}
                  color={filterType === "update" ? "info" : "default"}
                  variant={filterType === "update" ? "filled" : "outlined"}
                  label={`Update (${submitRows.filter(r => r.isUpdate).length})`}
                  sx={{ cursor: "pointer" }}
                />
              </ToggleButton>
              <ToggleButton value="skipped" aria-label="skipped">
                <Chip
                  component="span"
                  size="small"
                  icon={<HourglassEmptyIcon />}
                  color={filterType === "skipped" ? "warning" : "default"}
                  variant={filterType === "skipped" ? "filled" : "outlined"}
                  label={`Skipped (${skippedCount})`}
                  sx={{ cursor: "pointer" }}
                />
              </ToggleButton>
              <ToggleButton value="error" aria-label="errors">
                <Chip
                  component="span"
                  size="small"
                  icon={<ErrorOutlineIcon />}
                  color={filterType === "error" ? "error" : "default"}
                  variant={filterType === "error" ? "filled" : "outlined"}
                  label={`Errors (${invalidRows.length})`}
                  sx={{ cursor: "pointer" }}
                />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Chip icon={<CheckCircleIcon />} color="success" label={`${submitRows.length} to submit`} />
            <Chip variant="outlined" label={`${createCount} new`} />
            <Chip variant="outlined" label={`${updateCount} update(s)`} />
            {skippedCount > 0 && (
              <Chip
                variant="outlined"
                color="warning"
                label={`${skippedCount} existing skipped (no qty)`}
              />
            )}
            {invalidRows.length > 0 && (
              <Chip
                icon={<ErrorOutlineIcon />}
                color="error"
                label={`${invalidRows.length} row(s) with errors`}
              />
            )}
          </Box>

          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Product Name</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>Product Code</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Category</TableCell>
                  <TableCell align="right" sx={{ minWidth: 110 }}>Buying Price</TableCell>
                  <TableCell align="right" sx={{ minWidth: 110 }}>Selling Price</TableCell>
                  <TableCell align="right" sx={{ minWidth: 90 }}>Qty</TableCell>
                  <TableCell align="center">Service</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No rows match the selected filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row) => {
                    const isSkipped = row.isUpdate && row.quantityBlank;
                    return (
                      <TableRow
                        key={row.rowNumber}
                        hover
                        sx={{ opacity: isSkipped ? 0.4 : 1 }}
                      >
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={row.isUpdate ? "info" : "default"}
                            label={
                              isSkipped
                                ? "Skipped"
                                : row.isUpdate
                                ? `Update #${row.productId}`
                                : "New"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            variant="standard"
                            value={row.productName}
                            onChange={(e) =>
                              updateRowField(row.rowNumber, "Product Name", e.target.value)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            variant="standard"
                            value={row.productCode || ""}
                            onChange={(e) =>
                              updateRowField(row.rowNumber, "Product Code", e.target.value)
                            }
                            placeholder="Product code"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            variant="standard"
                            value={row.categoryId ?? ""}
                            onChange={(e) => {
                              const cat = categoryById(e.target.value);
                              updateRowField(
                                row.rowNumber,
                                "Select Category ▼",
                                cat ? cat.name : ""
                              );
                            }}
                            sx={{ minWidth: 140 }}
                          >
                            <MenuItem value="">
                              <em>Select…</em>
                            </MenuItem>
                            {categories.map((c) => (
                              <MenuItem key={c.id} value={c.id}>
                                {c.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            variant="standard"
                            type="number"
                            value={Number.isNaN(row.costPrice) ? "" : row.costPrice}
                            onChange={(e) =>
                              updateRowField(row.rowNumber, "Average Buying Price", e.target.value)
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            variant="standard"
                            type="number"
                            value={Number.isNaN(row.sellingPrice) ? "" : row.sellingPrice}
                            onChange={(e) =>
                              updateRowField(row.rowNumber, "Average Selling Price", e.target.value)
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            variant="standard"
                            type="number"
                            value={row.quantityBlank ? "" : (Number.isNaN(row.quantity) ? "" : row.quantity)}
                            onChange={(e) =>
                              updateRowField(row.rowNumber, "Quantity", e.target.value)
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox
                            size="small"
                            checked={row.isService}
                            onChange={(e) =>
                              updateRowField(
                                row.rowNumber,
                                "Is Service",
                                e.target.checked ? "TRUE" : "FALSE"
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {isSkipped ? (
                                <Tooltip title="Existing product with no quantity supplied">
                                    <Chip
                                        size="small"
                                        color="warning"
                                        label="Skipped"
                                    />
                                </Tooltip>
                            ) : row.isValid ? (
                            <Chip size="small" color="success" label="OK" />
                          ) : (
                            <Tooltip title={row.errors.join("; ")}>
                              <Chip size="small" color="error" label="Error" />
                            </Tooltip>
                          )}
                          
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => removeRow(row.rowNumber)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Step 4: Submit */}
      {isSubmitting && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Submitting {submitProgress.done} / {submitProgress.total}...
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(submitProgress.done / Math.max(submitProgress.total, 1)) * 100}
          />
        </Box>
      )}

      {rows.length > 0 && !isParsing && (
        <Box sx={{ textAlign: "right" }}>
          <Button sx={{ mr: 2 }} onClick={resetFlow} disabled={isSubmitting}>
            Start over
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={submitRows.length === 0 || isSubmitting}
            onClick={submitAll}
          >
            {isSubmitting
              ? "Submitting..."
              : `Submit ${submitRows.length} product(s)`}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default InventoryBulkUpload;